import type { ConfirmationModalProps } from '../components/ConfirmationModal';

interface PendingConfirmation {
    id: string;
    props: Omit<ConfirmationModalProps, 'onConfirm' | 'onCancel'>;
    resolve: (value: boolean) => void;
}

class ConfirmationService {
    private pendingConfirmations: PendingConfirmation[] = [];

    /**
     * Show a confirmation modal and return a promise that resolves to true if confirmed
     */
    confirm(
        message: string,
        options?: {
            confirmText?: string;
            cancelText?: string;
            isDestructive?: boolean;
        }
    ): Promise<boolean> {
        return new Promise((resolve) => {
            const id = `${Date.now()}-${Math.random()}`;
            const confirmation: PendingConfirmation = {
                id,
                props: {
                    message,
                    confirmText: options?.confirmText,
                    cancelText: options?.cancelText,
                    isDestructive: options?.isDestructive
                },
                resolve
            };

            this.pendingConfirmations.push(confirmation);

            // Dispatch event to update the confirmation modal
            const event = new CustomEvent('app-confirmation-request', {
                detail: { id }
            });
            window.dispatchEvent(event);
        });
    }

    /**
     * Get the next pending confirmation
     */
    getNextConfirmation(): PendingConfirmation | null {
        return this.pendingConfirmations[0] || null;
    }

    /**
     * Resolve a confirmation
     */
    resolveConfirmation(id: string, confirmed: boolean) {
        const index = this.pendingConfirmations.findIndex(c => c.id === id);
        if (index !== -1) {
            const confirmation = this.pendingConfirmations[index];
            this.pendingConfirmations.splice(index, 1);
            confirmation.resolve(confirmed);

            // Dispatch event to update the confirmation modal
            const event = new CustomEvent('app-confirmation-resolved', {
                detail: { id }
            });
            window.dispatchEvent(event);
        }
    }
}

export const confirmationService = new ConfirmationService();

